import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
   getCurrentUserIsAdminUser,
   getCurrentUserIsSystemUser,
} from 'src/app/authentication/state/authentication.selector';
import { Detail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { getUserPrivileges } from 'src/app/privileges/privileges.selector';
import { ExtensionGroupOption } from '../../extension-groups-register/models/extension-group-option.interface';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { PrivilegeView } from '../../role-details/models/role-detail-view.model';
import { getPrivileges } from '../../role-details/state/role-detail.selectors';
import { UserDetailSaveEvent } from '../models/user-detail-form-submit-event';
import { UserDetailView } from '../models/user-detail-view.model';
import { UserPrivileges } from '../models/user-privileges';
import { UserVerificationEmailRequestModel } from '../models/user-verification-email-request.model';
import { UserDetailFormService } from '../services/user-detail-form.service';
import * as UserDetailActions from '../state/user-detail.actions';
import {
   getUserCallState,
   getUserDetailExtensionGroups,
   getUserDetailExtensions,
   getUserDetailSelector,
} from '../state/user-detail.selectors';

@Component({
    encapsulation: ViewEncapsulation.None,
    host: { class: 'entity-detail' },
    providers: [UserDetailFormService, { provide: FormsBase, useExisting: UserDetailFormService }],
    selector: 'ndt-user-detail',
    templateUrl: './user-detail.component.html',
    standalone: false
})
export class UserDetailComponent extends Detail<UserDetailFormService> implements OnInit {
   user$: Observable<UserDetailView | null>;

   userPrivileges$: Observable<UserPrivileges>;

   privileges$: Observable<PrivilegeView[] | null>;

   extensions$: Observable<ExtensionOption[]>;

   extensionGroups$: Observable<ExtensionGroupOption[]>;

   accountId$: Observable<number | null>;

   isAdminUser$: Observable<boolean>;

   isSystemUser$: Observable<boolean>;

   constructor(public store: Store, public formService: UserDetailFormService, private router: Router) {
      super(store, formService);
   }

   ngOnInit(): void {
      this.user$ = this.store.pipe(select(getUserDetailSelector));
      this.userPrivileges$ = this.store.pipe(select(getUserPrivileges));
      this.privileges$ = this.store.pipe(select(getPrivileges));
      this.callState$ = this.store.pipe(select(getUserCallState));
      this.extensions$ = this.store.pipe(select(getUserDetailExtensions));
      this.isAdminUser$ = this.store.pipe(select(getCurrentUserIsAdminUser));
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
      this.accountId$ = this.store.pipe(
         select(getUserDetailSelector),
         map((user) => {
            if (user?.isSystemUser) {
               return null;
            }
            return user?.account?.accountId ?? null;
         })
      );
      this.extensionGroups$ = this.store.pipe(select(getUserDetailExtensionGroups));
      this.store.dispatch(UserDetailActions.userDetailLoad());
      this.store.dispatch(UserDetailActions.privilegesLoad());

      this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((e) => {
         if (e instanceof NavigationEnd) {
            this.store.dispatch(UserDetailActions.userDetailLoad());
         }
      });
   }

   onFormSaveCanceled(): void {
      this.store.dispatch(UserDetailActions.userDetailCancel());
   }

   onFormSubmitted(event: UserDetailSaveEvent) {
      this.store.dispatch(UserDetailActions.saveUserDetail(event));
   }

   onDeleteUser(event: { userId: number; name: string }) {
      this.store.dispatch(UserDetailActions.deleteUser(event));
   }

   onUnlockUser(event: { userId: number; name: string }) {
      this.store.dispatch(UserDetailActions.unlockUser(event));
   }

   onResendVerificationEmail(event: UserVerificationEmailRequestModel) {
      this.store.dispatch(UserDetailActions.resendVerificationEmail(event));
   }
}
